<template>
  <button
    :class="buttonClasses"
    :disabled="disabled"
    @click="$emit('click')"
    v-bind="$attrs"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  fullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  fullWidth: false
})

defineEmits<{
  click: []
}>()

const buttonClasses = computed(() => {
  const baseClasses = [
    'font-bold',
    'border-4',
    'border-black',
    'transition-all',
    'duration-150',
    'transform',
    'active:translate-x-1',
    'active:translate-y-1',
    'active:shadow-none',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:active:translate-x-0',
    'disabled:active:translate-y-0',
    'uppercase',
    'tracking-wider'
  ]
  
  // Size classes
  const sizeClasses = {
    sm: ['px-3', 'py-2', 'text-sm', 'shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]'],
    md: ['px-4', 'py-3', 'text-base', 'shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'],
    lg: ['px-6', 'py-4', 'text-lg', 'shadow-[5px_5px_0px_0px_rgba(0,0,0,1)]'],
    xl: ['px-8', 'py-5', 'text-xl', 'shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]']
  }
  
  // Variant classes
  const variantClasses = {
    primary: ['bg-blue-400', 'hover:bg-blue-500'],
    secondary: ['bg-gray-300', 'hover:bg-gray-400'],
    danger: ['bg-red-400', 'hover:bg-red-500'],
    success: ['bg-green-400', 'hover:bg-green-500'],
    warning: ['bg-yellow-400', 'hover:bg-yellow-500']
  }
  
  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []
  
  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...widthClasses
  ]
})
</script> 